import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Trash2 } from '@/components/icons';
import { api, errorMessage } from '@/lib/api';
import { isRunDeletable, runShortId, runTitle } from '@/lib/format';
import type { Run } from '@/lib/types';
import { Button, useConfirm, useToast } from '@/components/ui';
import { RunsTable } from './RunsTable';

const plural = (n: number) => `${n} report${n === 1 ? '' : 's'}`;

/**
 * Runs list with delete actions: a bin per finished run plus checkboxes and "Delete selected" for
 * removing several reports at once. Without delete rights it renders the plain list.
 */
export function DeletableRunsTable({
  runs,
  showProject = true,
  canDelete,
}: {
  runs: Run[];
  showProject?: boolean;
  canDelete: (run: Run) => boolean;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const confirm = useConfirm();
  const [selected, setSelected] = useState<Set<string>>(() => new Set());

  const deletableIds = useMemo(() => runs.filter((r) => isRunDeletable(r, canDelete)).map((r) => r.id), [runs, canDelete]);
  // Only count selections that are still listed (filters, refetches and deletions change the list).
  const chosen = deletableIds.filter((id) => selected.has(id));
  const allChosen = deletableIds.length > 0 && chosen.length === deletableIds.length;

  const selectAll = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (selectAll.current) selectAll.current.indeterminate = chosen.length > 0 && !allChosen;
  }, [chosen.length, allChosen]);

  const remove = useMutation({
    mutationFn: async (ids: string[]) => {
      const results = await Promise.allSettled(ids.map((id) => api.deleteRun(id)));
      const failed = results.flatMap((r) => (r.status === 'rejected' ? [r.reason as unknown] : []));
      return { deleted: ids.length - failed.length, failed };
    },
    onSuccess: ({ deleted, failed }, ids) => {
      if (failed.length === 0) toast.success(`${plural(deleted)} deleted`);
      else toast.error(`${plural(failed.length)} could not be deleted: ${errorMessage(failed[0])}${deleted ? ` (${deleted} deleted)` : ''}`);
      for (const id of ids) qc.removeQueries({ queryKey: ['run', id] });
      qc.invalidateQueries({ queryKey: ['reports'] });
      qc.invalidateQueries({ queryKey: ['runs'] });
      setSelected(new Set());
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  const deleteOne = async (run: Run) => {
    const ok = await confirm({
      title: 'Delete this report?',
      message: (
        <>
          <strong className="font-semibold text-slate-900 dark:text-white">{runTitle(run)}</strong> (run {runShortId(run)}) and its screenshots
          and videos will be deleted. This cannot be undone.
        </>
      ),
      confirmLabel: 'Delete report',
    });
    if (ok) remove.mutate([run.id]);
  };
  const deleteChosen = async () => {
    const ids = chosen;
    const ok = await confirm({
      title: `Delete ${plural(ids.length)}?`,
      message: 'The selected reports and their screenshots and videos will be deleted. This cannot be undone.',
      confirmLabel: `Delete ${plural(ids.length)}`,
    });
    if (ok) remove.mutate(ids);
  };
  const toggle = (run: Run) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (!next.delete(run.id)) next.add(run.id);
      return next;
    });

  if (deletableIds.length === 0) return <RunsTable runs={runs} showProject={showProject} />;

  return (
    <>
      <div className="no-print flex min-h-12 flex-wrap items-center gap-3 border-b border-slate-100 py-2 pr-5 dark:border-slate-800">
        <label className="flex items-center gap-3 text-sm text-slate-600 dark:text-slate-300">
          <span className="flex w-10 justify-end">
            <input
              ref={selectAll}
              type="checkbox"
              checked={allChosen}
              onChange={() => setSelected(allChosen ? new Set() : new Set(deletableIds))}
              className="size-4.5 rounded-bar border-border accent-primary"
            />
          </span>
          {chosen.length ? `${chosen.length} selected` : 'Select all'}
        </label>
        {chosen.length > 0 && (
          <Button
            size="sm"
            variant="danger"
            className="ml-auto"
            onClick={deleteChosen}
            loading={remove.isPending}
            icon={<Trash2 className="size-4" />}
          >
            Delete {plural(chosen.length)}
          </Button>
        )}
      </div>
      <RunsTable
        runs={runs}
        showProject={showProject}
        canDelete={canDelete}
        onDelete={deleteOne}
        selected={new Set(chosen)}
        onToggleSelect={toggle}
      />
    </>
  );
}
